/**
 * @Author:
 * @Description:
 * @DateTime: Created in 2020-12-02 08:50:50
 */

import * as Vue from 'vue-tsx-support';
import { Component, Prop } from 'vue-property-decorator';
import 'vue-tsx-support/enable-check';
import Modal from 'src/types/iview/Modal';
import Button from 'src/types/iview/Button';
import { modalFactory } from 'src/util/vueExtend';
import { createNamespace, createBEM } from 'src/util/bem';
import './selectLocationModal.less';
import Input from 'src/types/iview/Input';
import { applyOfficialMapStyle, applyTdtTheme, createLngLat, createLocationPoint, ensureTianDiTuLoaded, formatLngLat, getLat, getLng, getT, TDT_KEY } from 'src/util/tianditu';

let bem = createBEM('select-location');

export namespace SelectLocationVOI {
    export interface PropsI {
        data: {location_point: string, areaNames: string[], address: string};
        saveSuccess?(str: string, areaNames: string[], address: string): void;
        cancel?(): void;
    }
}

@Component
class SelectLocationModal extends Vue.Component<SelectLocationVOI.PropsI> {
    @Prop({
        type: Function,
    })
    cancel!: () => void;

    @Prop({
        type: Object,
    })
    data!: {location_point: string, areaNames: string[], address: string};

    @Prop({
        type: Function,
    })
    saveSuccess!: (str: string, areaNames: string[], address: string) => void;

    visible: boolean = false;
    loading: boolean = false;
    map: any;
    location: string = '';
    areaNames: string[] = [];
    address: string = '';
    inputAddress: string = '';
    marker: any = null;
    geocoder: any = null;

    close() {
        this.cancel && this.cancel();
        this.closeModal();
    }

    closeModal() {
        this.visible = false;
        this.$emit('close', false);
    }

    async initMap() {
        let TMap = await ensureTianDiTuLoaded();
        let map = new TMap.Map('map');
        this.map = map;
        applyTdtTheme('map');
        map.enableScrollWheelZoom(true);
        applyOfficialMapStyle(map, 'indigo');
        this.geocoder = new TMap.Geocoder();
        map.addEventListener('click', (e) => {
            this.setLocationByPoint(e.lnglat);
        });
        if (this.data.location_point) {
            let point = createLocationPoint(this.data.location_point);
            if (point) {
                this.updateMarker(point);
                map.centerAndZoom(point, 12);
            }
        } else {
            map.centerAndZoom(createLngLat(116.404, 39.915), 12);
        }
    }

    mounted() {
        this.location = this.data.location_point;
        this.areaNames = this.data.areaNames;
        this.address = this.data.address;
        setTimeout(() => {
            this.initMap();
        }, 500);
    }

    async save() {
        this.saveSuccess(this.location, this.areaNames, this.address);
        this.closeModal();
    }

    updateMarker(lngLat: any) {
        let TMap = getT();
        if (!this.marker) {
            this.marker = new TMap.Marker(lngLat);
            this.map.addOverLay(this.marker);
            return;
        }
        if (typeof this.marker.setLngLat === 'function') {
            this.marker.setLngLat(lngLat);
        } else {
            this.map.removeOverLay(this.marker);
            this.marker = new TMap.Marker(lngLat);
            this.map.addOverLay(this.marker);
        }
    }

    parseAreaNames(result: any) {
        let addressComponent = null;
        if (result) {
            if (result.addressComponent) {
                addressComponent = result.addressComponent;
            } else if (result.addressComponents) {
                addressComponent = result.addressComponents;
            } else if (result.result && result.result.addressComponent) {
                addressComponent = result.result.addressComponent;
            }
        }
        if (!addressComponent) {
            return [];
        }
        return [addressComponent.province, addressComponent.city, addressComponent.county || addressComponent.district].filter(Boolean);
    }

    parseAddress(result: any, areaNames: string[]) {
        let fullAddress = '';
        if (result) {
            fullAddress = result.address || result.formatted_address || '';
            if (!fullAddress && result.result) {
                fullAddress = result.result.formatted_address || result.result.address || '';
            }
            if (!fullAddress && typeof result.getAddress === 'function') {
                fullAddress = result.getAddress();
            }
        }
        let shortAddress = fullAddress;
        areaNames.forEach(i => {
            if (i) {
                shortAddress = shortAddress.replace(i, '');
            }
        });
        return shortAddress || fullAddress;
    }

    reverseGeocode(lngLat: any) {
        if (!this.geocoder || !lngLat) {
            return;
        }
        this.geocoder.getLocation(lngLat, (result) => {
            let areaNames = this.parseAreaNames(result);
            this.areaNames = areaNames;
            this.address = this.parseAddress(result, areaNames);
        });
    }

    setLocationByPoint(lngLat: any, zoom?: boolean) {
        if (!lngLat) {
            return;
        }
        this.location = formatLngLat(lngLat);
        this.updateMarker(lngLat);
        if (zoom) {
            this.map.centerAndZoom(lngLat, 15);
        }
        this.reverseGeocode(lngLat);
    }

    normalizeSearchPoint(poi: any) {
        if (!poi) {
            return null;
        }
        if (poi.lnglat) {
            return poi.lnglat;
        }
        if (poi.lonlat) {
            return poi.lonlat;
        }
        if (typeof poi.getLngLat === 'function') {
            return poi.getLngLat();
        }
        let lng = getLng(poi);
        let lat = getLat(poi);
        if (lng === null || lat === null) {
            return null;
        }
        return createLngLat(lng, lat);
    }

    extractFirstPoi(result: any) {
        if (!result) {
            return null;
        }
        if (typeof result.getPois === 'function') {
            let pois = result.getPois();
            return pois && pois.length ? pois[0] : null;
        }
        if (typeof result.getPoi === 'function') {
            return result.getPoi(0);
        }
        if (result.pois && result.pois.length) {
            return result.pois[0];
        }
        return null;
    }

    async geocodeAddress(keyword: string) {
        let ds = encodeURIComponent(JSON.stringify({
            keyWord: keyword,
        }));
        let response = await fetch(`https://api.tianditu.gov.cn/geocoder?ds=${ds}&tk=${TDT_KEY}`);
        let result = await response.json();
        if (!result || String(result.status) !== '0' || !result.location) {
            return null;
        }
        return createLngLat(result.location.lon, result.location.lat);
    }

    fallbackSearch(keyword: string) {
        let TMap = getT();
        let local = new TMap.LocalSearch(this.map, {
            pageCapacity: 1,
            onSearchComplete: (result) => {
                let poi = this.extractFirstPoi(result);
                let point = this.normalizeSearchPoint(poi);
                if (!point) {
                    return;
                }
                this.setLocationByPoint(point, true);
            },
        });
        local.search(keyword);
    }

    async refreshAddress() {
        let keyword = (this.inputAddress || '').trim();
        if (!keyword) {
            return;
        }
        try {
            let point = await this.geocodeAddress(keyword);
            if (point) {
                this.setLocationByPoint(point, true);
                return;
            }
        } catch (error) {
        }
        this.fallbackSearch(keyword);
    }

    render(h) {
        return (
            <Modal class={createNamespace('modal-fix-height')} title={'\u83b7\u53d6\u7ecf\u7eac\u5ea6'} width="1000" v-model={this.visible} mask-closable={false} on-on-cancel={this.close}>
                <div class={bem('wrapper')}>
                    <div class={bem('top-bar')}>
                        <div class={bem('address')}>
                            <span class={createNamespace('mr10')}>{'\u7ecf\u7eac\u5ea6\uff1a'}{this.location}</span>
                            <span>{'\u5730\u5740\uff1a'}{this.areaNames.join('') + this.address}</span>
                        </div>
                        <div class={bem('title')}>{'\u5f53\u524d\u4f4d\u7f6e'}</div>
                        <Input style="width:20%;right:10px;" v-model={this.inputAddress} placeholder={'\u8bf7\u8f93\u5165\u5730\u5740'}/>
                        <Button type="primary" on-click={this.refreshAddress} style="margin-right: 5px;">
                            {'\u641c\u7d22'}
                        </Button>
                    </div>
                    <div id="map" style="height:calc(100% + 25px);" />
                </div>
                <div slot="footer">
                    <Button type="primary" ghost on-click={this.close}>
                        {'\u53d6\u6d88'}
                    </Button>
                    <Button type="primary" on-click={this.save} loading={this.loading}>
                        {'\u4fdd\u5b58'}
                    </Button>
                </div>
            </Modal>
        );
    }
}

export default function alertSelectLocationModal(data: {location_point: string, areaNames: string[], address: string}): Promise<{areaNames: string[], address: string, location: string }|null> {
    return new Promise((resolve, reject) => {
        modalFactory<SelectLocationVOI.PropsI>(SelectLocationModal)({
            data,
            saveSuccess(location: string, areaNames: string[], address: string) {
                resolve({areaNames, address, location });
            },
            cancel() {
                resolve(null);
            },
        });
    });
}
